import { useEffect, useRef, useState } from "react";
import {
  AmbientLight,
  AnimationMixer,
  Box3,
  Clock,
  DirectionalLight,
  Group,
  PerspectiveCamera,
  Scene,
  Vector3,
  WebGLRenderer,
  type AnimationAction,
  type AnimationClip,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { MASCOT_GLB_URL } from "../mascot/asset";

type Props = {
  width: number;
  height: number;
  /** Animation clip name to play. The component matches case-insensitively
   *  and falls back to the first clip if no name matches — so callers can
   *  request "walk" / "idle" / "talk" without knowing the exact export
   *  name from the GLB. */
  animation: string;
  /** Called once when the GLB has loaded successfully. */
  onLoaded?: (clipNames: string[]) => void;
  /** Called if the GLB is missing or fails to parse. The parent uses
   *  this to show a CSS fallback so the tutorial still works without
   *  the asset present. */
  onError?: (err: unknown) => void;
};

export function MascotCanvas({ width, height, animation, onLoaded, onError }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mixerRef = useRef<AnimationMixer | null>(null);
  const actionsRef = useRef<Map<string, AnimationAction>>(new Map());
  const currentRef = useRef<AnimationAction | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const renderer = new WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height, false);
    renderer.setClearColor(0x000000, 0);
    host.appendChild(renderer.domElement);

    const scene = new Scene();
    const camera = new PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.set(0, 1.4, 3.2);
    camera.lookAt(0, 1.0, 0);

    scene.add(new AmbientLight(0xffffff, 0.7));
    const key = new DirectionalLight(0xfff4e0, 0.9);
    key.position.set(2, 4, 3);
    scene.add(key);

    const clock = new Clock();
    let raf = 0;
    let disposed = false;
    let loadedRoot: Group | null = null;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const dt = clock.getDelta();
      mixerRef.current?.update(dt);
      renderer.render(scene, camera);
    };
    tick();

    const loader = new GLTFLoader();
    loader.load(
      MASCOT_GLB_URL,
      (gltf) => {
        if (disposed) return;
        const root = gltf.scene;
        loadedRoot = root;

        // Center + scale to roughly fill the canvas regardless of the
        // source rig's units. Without this an FBX-converted axolotl
        // can land at 0.01x or 100x and render off-screen.
        const box = new Box3().setFromObject(root);
        const size = box.getSize(new Vector3());
        const center = box.getCenter(new Vector3());
        const longest = Math.max(size.x, size.y, size.z) || 1;
        const targetHeight = 1.6;
        const scale = targetHeight / longest;
        root.scale.setScalar(scale);
        root.position.sub(center.multiplyScalar(scale));
        root.position.y = -box.min.y * scale * 0.5;
        scene.add(root);

        const mixer = new AnimationMixer(root);
        mixerRef.current = mixer;
        const actions = new Map<string, AnimationAction>();
        gltf.animations.forEach((clip: AnimationClip) => {
          actions.set(clip.name.toLowerCase(), mixer.clipAction(clip));
        });
        actionsRef.current = actions;

        setReady(true);
        onLoaded?.(gltf.animations.map((c) => c.name));
      },
      undefined,
      (err) => {
        if (disposed) return;
        onError?.(err);
      },
    );

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      mixerRef.current?.stopAllAction();
      mixerRef.current = null;
      actionsRef.current.clear();
      currentRef.current = null;
      if (loadedRoot) scene.remove(loadedRoot);
      renderer.dispose();
      if (renderer.domElement.parentNode === host) {
        host.removeChild(renderer.domElement);
      }
    };
  }, [width, height, onLoaded, onError]);

  useEffect(() => {
    if (!ready) return;
    const actions = actionsRef.current;
    if (actions.size === 0) return;
    const wanted = animation.toLowerCase();
    let next = actions.get(wanted);
    if (!next) {
      // Fuzzy match: pick the first clip whose name contains the
      // requested keyword (handles "Idle_01" vs "idle"). If still no
      // hit, fall back to the first clip so the model is never frozen.
      for (const [name, action] of actions) {
        if (name.includes(wanted)) {
          next = action;
          break;
        }
      }
      if (!next) next = actions.values().next().value;
    }
    if (!next || next === currentRef.current) return;
    const previous = currentRef.current;
    next.reset().fadeIn(0.25).play();
    if (previous) previous.fadeOut(0.25);
    currentRef.current = next;
  }, [animation, ready]);

  return <div ref={hostRef} style={{ width, height }} aria-hidden="true" />;
}
